import React, { createContext, useState, useEffect, useContext } from 'react';
import { useLocation } from 'react-router-dom';
import { getBackendUrl } from '../api';

const CountryContext = createContext();

export const useCountry = () => {
    const context = useContext(CountryContext);
    if (!context) {
        throw new Error('useCountry must be used within a CountryProvider');
    }
    return context;
};

export const CountryProvider = ({ children }) => {
    const [selectedCountry, setSelectedCountry] = useState(null);
    const [countries, setCountries] = useState([]);
    const [loading, setLoading] = useState(true);
    const location = useLocation();

    // Get the current URL path and extract country suffix
    const getUrlSuffix = () => {
        const path = location.pathname;
        // Extract first segment after root (e.g., /us, /sa, /om)
        const match = path.match(/^\/([a-zA-Z0-9]{2,})(?:\/|$)/);
        return match ? match[1] : null;
    };

    // Match country by URL
    const matchCountryByUrl = (countriesList) => {
        const currentPath = location.pathname;
        const urlSuffix = getUrlSuffix();
        
        // Try to match by full URL or URL suffix
        let matched = null;
        
        for (const country of countriesList) {
            if (country.portalUrl) {
                // Check if current URL matches the country's portal URL
                const portalBase = 'https://parcels.shypv.com';
                
                // If URL is exactly the base (no suffix), match with UAE
                if (currentPath === '/' || currentPath === '' || !urlSuffix) {
                    if (country.portalUrl === portalBase || country.portalUrl === portalBase + '/') {
                        matched = country;
                        break;
                    }
                } else {
                    // Check if portal URL ends with the current URL suffix
                    const countryUrlSuffix = country.portalUrl.replace(portalBase, '').replace('/', '');
                    if (countryUrlSuffix && countryUrlSuffix.toLowerCase() === urlSuffix.toLowerCase()) {
                        matched = country;
                        break;
                    }
                }
            }
        }
        
        return matched;
    };

    // Load countries and selected country based on URL
    useEffect(() => {
        const loadCountries = async () => {
            try {
                const backendUrl = getBackendUrl();
                
                const response = await fetch(`${backendUrl}/countries/active`);
                
                const data = await response.json();
                
                if (data.status === 'success' && data.data) {
                    setCountries(data.data);
                    
                    // First, try to match country by URL
                    const urlMatchedCountry = matchCountryByUrl(data.data);
                    
                    if (urlMatchedCountry) {
                        setSelectedCountry(urlMatchedCountry);
                        console.log('Country selected by URL:', urlMatchedCountry.countryName);
                    } else {
                        // Fallback: Check localStorage
                        const savedCountryId = localStorage.getItem('selectedCountryId');
                        
                        if (savedCountryId) {
                            const saved = data.data.find(c => c.id === parseInt(savedCountryId));
                            if (saved) {
                                setSelectedCountry(saved);
                            } else {
                                // Use default country
                                const defaultCountry = data.data.find(c => c.isDefault === 1);
                                setSelectedCountry(defaultCountry || data.data[0]);
                            }
                        } else {
                            // Use default country from config
                            const defaultCountry = data.data.find(c => c.isDefault === 1);
                            setSelectedCountry(defaultCountry || data.data[0]);
                        }
                    }
                }
            } catch (error) {
                console.error('Error loading countries:', error);
                console.error('Backend not available. Please check your connection or contact support.');
                
                // Don't set any fallback - let the app handle the error state
                setCountries([]);
                setSelectedCountry(null);
            } finally {
                setLoading(false);
            }
        };

        loadCountries();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname]);

    // Save selected country to localStorage when it changes
    useEffect(() => {
        if (selectedCountry) {
            localStorage.setItem('selectedCountryId', selectedCountry.id);
            localStorage.setItem('selectedCountry', JSON.stringify(selectedCountry));
        }
    }, [selectedCountry]);

    const changeCountry = (countryId) => {
        const country = countries.find(c => c.id === countryId);
        if (country) {
            setSelectedCountry(country);
        }
    };

    // Parse cities to collect from comma-separated string
    const getCitiesArray = () => {
        if (selectedCountry?.citiesToCollect) {
            return selectedCountry.citiesToCollect
                .split(',')
                .map(city => city.trim())
                .filter(city => city);
        }
        // Return empty array if no country is selected or cities not configured
        return [];
    };

    // Parse transit time JSON
    const getTransitTime = (destinationCountry) => {
        if (selectedCountry?.transitTime) {
            try {
                const transitTimes = JSON.parse(selectedCountry.transitTime);
                return transitTimes[destinationCountry] || 'N/A';
            } catch (e) {
                console.error('Error parsing transit time:', e);
                return 'N/A';
            }
        }
        return 'N/A';
    };

    const value = {
        selectedCountry,
        countries,
        loading,
        changeCountry,
        getBackendUrl,
        currency: selectedCountry?.currency || 'AED',
        paymentGatewayCurrency: selectedCountry?.paymentGatewayCurrency || 'AED',
        shypvCommission: selectedCountry?.shypvCommission || 2,
        weightUnit: selectedCountry?.weightUnit || 'kg',
        dimensionUnit: selectedCountry?.dimensionUnit || 'cm',
        phone: selectedCountry?.phone || '',
        whatsapp: selectedCountry?.whatsapp || '',
        logoUrl: selectedCountry?.logoUrl || '',
        citiesToCollect: getCitiesArray(),
        carrier: selectedCountry?.carrier || 'DHL',
        getTransitTime,
        // Rate table URLs
        dhlExportRateUrl: selectedCountry?.dhlExportRateUrl || '',
        dhlImportRateUrl: selectedCountry?.dhlImportRateUrl || '',
        fedexExportRateUrl: selectedCountry?.fedexExportRateUrl || '',
        fedexImportRateUrl: selectedCountry?.fedexImportRateUrl || '',
        upsExportRateUrl: selectedCountry?.upsExportRateUrl || '',
        upsImportRateUrl: selectedCountry?.upsImportRateUrl || '',
        otherExportRateUrl: selectedCountry?.otherExportRateUrl || '',
        otherImportRateUrl: selectedCountry?.otherImportRateUrl || '',
    };

    return (
        <CountryContext.Provider value={value}>
            {children}
        </CountryContext.Provider>
    );
};

export default CountryContext;

